/** Shared helpers for category→pipeline stage translation (settings UI + tests). */

import {
  isLeadOutcome,
  outcomeFromStatus,
  statusMirrorFromOutcomeAndLabel,
  type LeadOutcome,
} from './leadPipeline';

export type StagePair = { from_stage_id: string; to_stage_id: string };

export type PipelineStagesLike = {
  id: string;
  name?: string;
  stages: ReadonlyArray<{ id: string; label: string }>;
};

/** Explicit stage translation when a category switches pipelines. */
export type CategoryPipelineStageMapInput = {
  category_key: string;
  from_pipeline_id: string;
  to_pipeline_id: string;
  stages: StagePair[];
};

/** Default mapping: same label in target pipeline, else first target stage. */
export function defaultStagePairsForPipelines(
  fromPipeline: PipelineStagesLike,
  toPipeline: PipelineStagesLike,
): StagePair[] {
  const fallback = toPipeline.stages[0];
  if (!fallback) return [];
  return fromPipeline.stages.map((from) => {
    const match = toPipeline.stages.find((s) => s.label === from.label);
    return { from_stage_id: from.id, to_stage_id: (match ?? fallback).id };
  });
}

export function validateCategoryStageMap(
  map: CategoryPipelineStageMapInput,
  pipelines: ReadonlyArray<PipelineStagesLike>,
):
  | {
      ok: true;
      from: PipelineStagesLike;
      to: PipelineStagesLike;
      stageIdMap: Map<string, string>;
    }
  | { ok: false; error: string } {
  const categoryKey = String(map.category_key ?? '').trim();
  if (!categoryKey) return { ok: false, error: 'category_key fehlt in stage_maps.' };

  const from = pipelines.find((p) => p.id === String(map.from_pipeline_id ?? '').trim());
  const to = pipelines.find((p) => p.id === String(map.to_pipeline_id ?? '').trim());
  if (!from) return { ok: false, error: 'Quell-Pipeline ungültig.' };
  if (!to) return { ok: false, error: 'Ziel-Pipeline ungültig.' };
  if (from.id === to.id) {
    return { ok: false, error: 'Quell- und Ziel-Pipeline sind identisch.' };
  }

  const pairs = Array.isArray(map.stages) ? map.stages : [];

  // Empty pipelines: no stage pairs possible — leads fall back / clear stage_id.
  if (from.stages.length === 0 || to.stages.length === 0) {
    if (pairs.length > 0) {
      return {
        ok: false,
        error:
          from.stages.length === 0
            ? `Pipeline „${from.name ?? from.id}“ hat keine Phasen — stages muss leer sein.`
            : `Pipeline „${to.name ?? to.id}“ hat keine Phasen — stages muss leer sein.`,
      };
    }
    return { ok: true, from, to, stageIdMap: new Map() };
  }

  const fromIds = new Set(from.stages.map((s) => s.id));
  const toIds = new Set(to.stages.map((s) => s.id));
  if (pairs.length === 0) {
    return { ok: false, error: 'stage_maps.stages darf nicht leer sein.' };
  }

  const stageIdMap = new Map<string, string>();
  for (const pair of pairs) {
    const fromId = String(pair?.from_stage_id ?? '').trim();
    const toId = String(pair?.to_stage_id ?? '').trim();
    if (!fromIds.has(fromId)) {
      return { ok: false, error: `Unbekannte Quell-Phase „${fromId}“.` };
    }
    if (!toIds.has(toId)) {
      return { ok: false, error: `Unbekannte Ziel-Phase „${toId}“.` };
    }
    stageIdMap.set(fromId, toId);
  }

  for (const stage of from.stages) {
    if (!stageIdMap.has(stage.id)) {
      return {
        ok: false,
        error: `Phase „${stage.label}“ der Quell-Pipeline fehlt in der Übersetzung.`,
      };
    }
  }

  return { ok: true, from, to, stageIdMap };
}

export function applyStageMapToLead(params: {
  stageId: string | null | undefined;
  status?: string | null;
  outcome?: unknown;
  stageIdMap: Map<string, string>;
  toPipeline: PipelineStagesLike;
}): { stage_id: string | null; status: string; outcome: LeadOutcome } {
  const outcome = isLeadOutcome(params.outcome)
    ? params.outcome
    : outcomeFromStatus(params.status != null ? String(params.status) : null);

  const fallback = params.toPipeline.stages[0] ?? null;
  const currentId =
    params.stageId != null && String(params.stageId).trim() !== ''
      ? String(params.stageId).trim()
      : null;

  let newStageId: string | null = null;
  if (currentId && params.stageIdMap.has(currentId)) {
    newStageId = params.stageIdMap.get(currentId) ?? null;
  } else if (fallback) {
    newStageId = fallback.id;
  }

  const label = newStageId
    ? (params.toPipeline.stages.find((s) => s.id === newStageId)?.label ?? null)
    : null;

  return {
    stage_id: newStageId,
    outcome,
    status: statusMirrorFromOutcomeAndLabel(outcome, label),
  };
}
